import { HttpClient, HttpParams } from '@angular/common/http';
import type { CotasBarrasResponse } from './models/cotas-barras.models';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { EMPTY, Observable, map, shareReplay, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { LocaleService } from '@core/i18n/locale.service';
import { BibliotecaResponse } from './models/biblioteca.models';
import type { DatatastoriesCargoResponse } from './models/mudanca-cargo.models';

const DATA_STORY_CATEGORY = 'Data Story';

@Injectable({ providedIn: 'root' })
export class DatastoryService {
  private readonly http = inject(HttpClient);
  private readonly localeService = inject(LocaleService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly url = `${environment.apiUrl}/biblioteca`;

  /** Cache por idioma da API (`pt` | `en`), espelhando o front-17. */
  private readonly cache = new Map<string, Observable<BibliotecaResponse[]>>();

  /** Cache de detalhe por `id-lang`; refCount evita reutilizar erro após nova visita. */
  private readonly detailCache = new Map<string, Observable<BibliotecaResponse>>();

  /**
   * Lista de Data Stories publicados.
   * Mesma chamada do `BibliotecaService.getDatastories()` do infogov-front-17:
   * `GET /biblioteca/Data Story?language={pt|en}`
   */
  getDatastories(): Observable<BibliotecaResponse[]> {
    if (!this.isBrowser) return EMPTY;

    const lang = this.localeService.currentLocale === 'en' ? 'en' : 'pt';
    let stream = this.cache.get(lang);

    if (!stream) {
      const params = new HttpParams().set('language', lang);
      stream = this.http
        .get<BibliotecaResponse[]>(
          `${this.url}/${encodeURIComponent(DATA_STORY_CATEGORY)}`,
          { params },
        )
        .pipe(
          map((stories) => stories.filter((s) => s.visivel)),
          shareReplay(1),
        );
      this.cache.set(lang, stream);
    }

    return stream;
  }

  /**
   * Detalhe de um Data Story por id numérico ou slug.
   * Mesma chamada do `BibliotecaService.getById()` do infogov-front-17.
   */
  getById(id: number | string): Observable<BibliotecaResponse> {
    // SSR/prerender: não completa nem emite — mantém a UI em loading até hidratar no browser.
    if (!this.isBrowser) return new Observable<BibliotecaResponse>();

    const lang = this.localeService.currentLocale === 'en' ? 'en' : 'pt';
    const resolvedId = typeof id === 'number' ? id : DATASTORY_ID_BY_SLUG[id] ?? id;
    const cacheKey = `${resolvedId}-${lang}`;
    let stream = this.detailCache.get(cacheKey);

    if (!stream) {
      const params = new HttpParams().set('language', lang);
      stream = this.http
        .get<BibliotecaResponse>(`${this.url}/${resolvedId}`, { params })
        .pipe(
          catchError((error) => {
            this.detailCache.delete(cacheKey);
            return throwError(() => error);
          }),
          shareReplay({ bufferSize: 1, refCount: true }),
        );
      this.detailCache.set(cacheKey, stream);
    }

    return stream;
  }

  /** Gráfico de barras interativo de cotas — espelha `BibliotecaService.getChartBarras()`. */
  getChartBarras(params: Record<string, unknown>): Observable<CotasBarrasResponse> {
    if (!this.isBrowser) return EMPTY;

    let httpParams = new HttpParams();
    const appendList = (key: string, apiKey: string, value: unknown) => {
      if (!value) return;
      if (Array.isArray(value) && value.length > 0) {
        value.forEach((v) => {
          httpParams = httpParams.append(apiKey, String(v));
        });
      } else if (typeof value === 'string') {
        httpParams = httpParams.append(apiKey, value);
      }
    };

    appendList('orgaoSuperior', 'orgao_superior', params['orgaoSuperior']);
    appendList('escolaridade', 'esc_cargo', params['escolaridade']);
    appendList('ano', 'ano', params['ano']);

    return this.http.get<CotasBarrasResponse>(
      `${environment.apiUrl}/datastories/grafico/barras`,
      { params: httpParams },
    );
  }

  /** Série de mudança de cargo por cargo — espelha `BibliotecaService.getMudancaoCargoByCargo()`. */
  getMudancaCargoByCargo(cargo?: string): Observable<DatatastoriesCargoResponse> {
    if (!this.isBrowser) return EMPTY;

    const params = new HttpParams().set('cargo', cargo ?? '');
    return this.http.get<DatatastoriesCargoResponse>(
      `${environment.apiUrl}/datastories`,
      { params },
    );
  }
}

/** Mapa slug → id — espelha `datastoriesIdMap` do infogov-front-17. */
const DATASTORY_ID_BY_SLUG: Record<string, number> = {
  'analise-compras': 1,
  'cargos-confianca': 2,
  'transferencias-voluntarias': 3,
  'servicos-automacao': 13,
  'metrica-complexidade': 15,
  'metrica-restritividade': 16,
  'metrica-influencia': 17,
  'metrica-popularidade': 18,
  'producao-normativa': 19,
  'mudanca-cargo': 20,
  'queimadas': 21,
  'favelas': 22,
  'cotas': 23,
  'brasil-solar': 24,
  'transicao-energia': 25,
  'transicao-eletrica': 25,
  'genero': 26,
  'mobilidade-social': 27,
};
